import { readBillableDuration } from './billable-hours';
import {
  DURATION_NOT_A_NUMBER,
  formatDuration,
  inlineDurationPayload,
  parseDuration,
  type DurationReading,
  type EditPayload,
  type StoredEntryTimes,
} from './linked-time-fields';

/**
 * The hour boxes of Reports → Show entries (both cells) and the Edit dialog's
 * Billable Hours box: what they are seeded with, how they read, and what a
 * commit sends. No grammar of its own — the boxes read exactly like the Log
 * Time Duration box (`parseDuration`; HW-974 design, decision 5), Billable
 * through `readBillableDuration`, which only adds zero. Here rather than in
 * the components because vitest is node-env in this repo. [HW-974]
 */

export type HoursField = 'logged' | 'billable';

/**
 * What an opened box starts with: the grammar's own spelling (`2h 30m`), not
 * the two-decimal hours the cell prints — `parseDuration` reads `2.50` as
 * 2h 50m, so seeding the printed `2.50` would turn a nudge of the digits into
 * the wrong length. Minute resolution; the text comparison in
 * `readHoursChange` keeps an untouched box from rounding away seconds.
 */
export function hoursBoxSeed(seconds: number): string {
  return formatDuration(seconds);
}

/** Logged reads as the Duration box; Billable the same, zero allowed. */
export function readHoursBox(raw: string, field: HoursField): DurationReading {
  return field === 'billable' ? readBillableDuration(raw) : parseDuration(raw);
}

export type HoursChange =
  | { kind: 'empty' }
  | { kind: 'unchanged' }
  | { kind: 'error'; message: string }
  | { kind: 'changed'; seconds: number };

/**
 * What a box holds against what it was seeded with.
 *
 * Unchanged when the text still reads as seeded — compared as TEXT first,
 * because the seed is minute-resolution and an entry's sub-minute seconds (a
 * timer stopped at 2h 0m 12s) would otherwise look edited and be rounded away
 * by a click in and out — or when a retyped value comes to the stored seconds.
 * An empty box is its own answer: the cell cancels on it, the dialog refuses it.
 */
export function readHoursChange(input: {
  field: HoursField;
  raw: string;
  seeded: string;
  storedSeconds: number;
}): HoursChange {
  const text = input.raw.trim();
  if (text === '') return { kind: 'empty' };
  if (text === input.seeded.trim()) return { kind: 'unchanged' };
  const reading = readHoursBox(text, input.field);
  if (reading.seconds === null) {
    return { kind: 'error', message: reading.error ?? DURATION_NOT_A_NUMBER };
  }
  if (reading.seconds === input.storedSeconds) return { kind: 'unchanged' };
  return { kind: 'changed', seconds: reading.seconds };
}

/** Said when a stored entry names no day to judge a new length on. */
export const ENTRY_NOT_SAVABLE = 'This entry could not be saved.';

export type InlineCommit =
  | { kind: 'cancel' }
  | { kind: 'refuse'; message: string }
  | {
      kind: 'save';
      payload: { duration: number } | EditPayload | { billableDuration: number };
    };

/**
 * What a Show entries cell does when its box is left — by Enter or by blur.
 * An emptied or unchanged box sends nothing; a refused one says why and stays
 * open (`hoursBoxEvent`); a changed one sends its own field. Billable sends
 * `billableDuration` alone — it has no clock times. Logged sends `duration`,
 * plus the start moved back when the new length would carry the entry past
 * its midnight, as the Edit dialog would (`inlineDurationPayload`). [HW-974]
 */
export function inlineHoursCommit(input: {
  field: HoursField;
  raw: string;
  seeded: string;
  storedSeconds: number;
  stored: StoredEntryTimes;
}): InlineCommit {
  const change = readHoursChange(input);
  switch (change.kind) {
    case 'empty':
    case 'unchanged':
      return { kind: 'cancel' };
    case 'error':
      return { kind: 'refuse', message: change.message };
    case 'changed': {
      if (input.field === 'billable') {
        return { kind: 'save', payload: { billableDuration: change.seconds } };
      }
      const payload = inlineDurationPayload(input.stored, change.seconds);
      return payload ? { kind: 'save', payload } : { kind: 'refuse', message: ENTRY_NOT_SAVABLE };
    }
  }
}

/** An open cell box: what it holds, the reason it was refused, whether its
 *  save is out, and the text the server last refused with its reason. Null is
 *  a closed box. */
export interface HoursBox {
  text: string;
  error: string | null;
  saving: boolean;
  refused: { text: string; message: string } | null;
}

/** How a box was left: Enter, or focus moving away. */
export type HoursBoxCommitVia = 'enter' | 'blur';

export type HoursBoxEvent =
  | { type: 'open'; seed: string }
  | { type: 'type'; text: string }
  | { type: 'cancel' }
  | { type: 'commit'; via: HoursBoxCommitVia; decision: InlineCommit }
  | { type: 'saved' }
  | { type: 'failed'; message: string };

/**
 * The box of a Show entries cell, from open to closed.
 *
 * A refusal — the grammar's or the server's — keeps the box open on what was
 * typed, with the reason under it, as the Edit dialog keeps its boxes: thrown
 * away with a toast, `2.60` had to be typed again from the seed to be fixed.
 * Typing clears the reason; Escape closes the box. While a save is out the
 * box holds still — typing, Escape and a second commit (the blur that can
 * follow Enter) change nothing — and it closes only when the save has landed,
 * so a refusal still has an open box to stand under.
 *
 * A text the server refused is not sent again by a blur: the box stays as it
 * is, with the server's reason (shown again if typing away and back cleared
 * it), so clicking elsewhere does not repeat a request already answered with
 * a refusal. Enter with that text is the reader's explicit
 * retry and is sent; a different text is a new request, by Enter or by blur.
 * The caller sends only when the commit left the box saving. [HW-974]
 */
export function hoursBoxEvent(box: HoursBox | null, event: HoursBoxEvent): HoursBox | null {
  if (event.type === 'open') {
    return box ?? { text: event.seed, error: null, saving: false, refused: null };
  }
  if (box === null) return null;
  if (box.saving) {
    if (event.type === 'saved') return null;
    if (event.type === 'failed') {
      return {
        ...box,
        error: event.message,
        saving: false,
        refused: { text: box.text, message: event.message },
      };
    }
    return box;
  }
  switch (event.type) {
    case 'type':
      return { ...box, text: event.text, error: null };
    case 'cancel':
      return null;
    case 'commit':
      if (event.decision.kind === 'cancel') return null;
      if (event.decision.kind === 'refuse') return { ...box, error: event.decision.message };
      if (event.via === 'blur' && box.text === box.refused?.text) {
        return box.error === box.refused.message ? box : { ...box, error: box.refused.message };
      }
      return { ...box, error: null, saving: true };
    default:
      return box;
  }
}

/**
 * Where a server refusal of a cell's save is told: under the box only while
 * the cell is still on screen and still the table's open cell. Otherwise
 * nobody sees the box — the reader opened another cell, or turned the page,
 * switched to the summary or changed a filter and the row unmounted (its
 * `active` flag frozen as it was) — and since the update hook suppresses the
 * global toast, the refusal would be lost with the old value silently kept.
 * Those go to a toast. [HW-974]
 */
export function refusalShowsInBox(cell: { mounted: boolean; active: boolean }): boolean {
  return cell.mounted && cell.active;
}

/**
 * What the Edit dialog's Billable box shows. On an entry whose billable was
 * never adjusted, and until the holder types in the box, a Save that changes
 * the length makes the box read that length — the figure the server will
 * store, since an unadjusted billable follows a changed duration — so
 * changing Duration moves it. With the length unchanged the server keeps the
 * stored billable, and so does the box. An adjusted entry keeps its figure,
 * and a box the holder has typed in shows what was typed. While the form
 * cannot be saved nothing would be stored, so the box shows its own text.
 *
 * Display only: an untouched box's own text stays the seed, so it reads as
 * unchanged and is not sent, and the server applies its rule. [HW-974]
 */
export function billableBoxText(input: {
  adjusted: boolean;
  touched: boolean;
  typed: string;
  /** The entry's stored length. */
  storedDuration: number;
  /** The length a Save would send now; null while it may not be sent. */
  savedDuration: number | null;
}): string {
  const { adjusted, touched, typed, storedDuration, savedDuration } = input;
  if (adjusted || touched || savedDuration === null || savedDuration === storedDuration) {
    return typed;
  }
  return hoursBoxSeed(savedDuration);
}
